import { ICashlessRepository } from "domains/cashless/interfaces/ICashlessRepository";
import { Kiosk } from "domains/kiosk/aggregates/Kiosk";
import { IOrderRepository } from "domains/order/interfaces/repositories/IOrderRepository";
import { IOrderRepositoryDB } from "domains/order/interfaces/repositories/IOrderRepositoryDB";
import { IOrder } from "modules/order/domain/models/IOrder";
import { TransactionType } from "modules/payment/domain/models/TransactionType";
import { SyncOrderUseCase } from "./SyncOrderUseCase";

/**
 * Recargas cashless online pagas cujo RechargeAccount falhou.
 * Sem a fila, o cliente era cobrado e o saldo nunca entrava (o erro era só logado).
 */
const STORAGE_KEY = "@meep/pendingOnlineRecharges";
const MAX_AGE_MS = 2 * 24 * 60 * 60 * 1000;

export interface IPendingOnlineRecharge {
  orderId: string;
  transactionType: TransactionType;
  createdAt: number;
  attempts: number;
  lastError?: string;
}

const read = (): IPendingOnlineRecharge[] => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]");
  } catch {
    return [];
  }
};

const write = (items: IPendingOnlineRecharge[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
};

export const enqueuePendingOnlineRecharge = (orderId: string, transactionType: TransactionType, lastError?: string) => {
  const items = read();
  if (items.some((it) => it.orderId === orderId)) {
    return;
  }
  write([...items, { orderId, transactionType, createdAt: Date.now(), attempts: 0, lastError }]);
};

export const getPendingOnlineRecharges = () => read();

let processing = false;

/** Reenvia as recargas da fila. Retorna quantas foram enviadas. */
export const ProcessPendingOnlineRechargesUseCase = async (
  localOrderRepository: IOrderRepositoryDB,
  orderRepository: IOrderRepository,
  cashlessRepository: ICashlessRepository,
  kiosk: Kiosk
): Promise<number> => {
  if (processing) {
    return 0;
  }
  processing = true;
  let sent = 0;
  try {
    for (const item of read()) {
      const order = await localOrderRepository.findById(item.orderId);
      if (!order || Date.now() - item.createdAt > MAX_AGE_MS) {
        // Pedido apagado ou antigo demais: fica no histórico para conciliação manual
        write(read().filter((it) => it.orderId !== item.orderId));
        continue;
      }
      const result = await SyncOrderUseCase(
        orderRepository,
        cashlessRepository,
        order as unknown as IOrder,
        item.transactionType,
        kiosk,
        "Recharge"
      );
      if (result.success) {
        sent++;
        write(read().filter((it) => it.orderId !== item.orderId));
        await localOrderRepository.add(order, `Recarga reenviada (tentativa ${item.attempts + 1})`);
      } else {
        write(
          read().map((it) =>
            it.orderId === item.orderId ? { ...it, attempts: it.attempts + 1, lastError: result.message } : it
          )
        );
      }
    }
  } finally {
    processing = false;
  }
  return sent;
};
